import React from 'react';
import { Solicitud } from '@/types/solicitud';

interface SimuladorSIACProps {
  solicitud: Solicitud;
  onSimular: () => Promise<void>;
  isSimulating: boolean;
}

const fmt = (n: number) => `$${new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2 }).format(n)}`;

function SiacRow({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="flex justify-between py-0.5 border-b border-gray-100 last:border-0">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className={`text-xs font-medium text-right ${cls || 'text-gray-900'}`}>{value}</span>
    </div>
  );
}

function SiacCard({ title, headerClass, children }: { title: string; headerClass: string; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col">
      <div className={`px-3 py-2 font-bold text-sm ${headerClass}`}>
        {title}
      </div>
      <div className="p-3 flex-1 space-y-0">
        {children}
      </div>
    </div>
  );
}

export function SimuladorSIAC({ solicitud, onSimular, isSimulating }: SimuladorSIACProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-indigo-800 text-base">Simulador SIAC</h3>
        <button
          type="button"
          className="px-5 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm shadow-sm transition-colors"
          onClick={onSimular}
          disabled={isSimulating}
        >
          {isSimulating ? 'Simulando...' : 'Simular'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Card: Datos Generales */}
        <SiacCard title="Datos Generales" headerClass="bg-blue-100 text-blue-800">
          <SiacRow label="Origen del Contrato" value={
            solicitud.producto === 'Nómina' ? 'NORMAL' :
            solicitud.producto === 'Adelanto de Nómina' ? 'DISPOSICIÓN REVOLVENTE' : ''
          } />
          <SiacRow label="Fecha de Contrato" value={solicitud.fechaEmisionContrato ? String(solicitud.fechaEmisionContrato).split('T')[0].split(' ')[0] : ''} />
          <SiacRow label="No. Control" value={solicitud.solicitudNumeroControl || 'Falta Asignar Número de Control'} cls={!solicitud.solicitudNumeroControl ? 'text-amber-600' : ''} />
          <SiacRow label="Tipo de Contrato" value={
            solicitud.producto === 'Adelanto de Nómina' ? 'REVOLVENTE' :
            solicitud.producto === 'Nómina' ? `NOMINA DESCUENTO ${(solicitud.frecuenciaDePago || '').toUpperCase()}` : ''
          } />
          <SiacRow label="Individual / Grupal" value="Individual" />
          <SiacRow label="Nombre del Cliente" value={`${solicitud.clienteNombre || ''} ${solicitud.clienteApellidoPaterno || ''} ${solicitud.clienteApellidoMaterno || ''}`.trim()} />
          <SiacRow label="Monto del Contrato ($)" value={fmt(solicitud.montoAutorizado ?? 0)} />
          <SiacRow label="Producto Financiero" value="Dejar vacío" />
          <SiacRow label="Tipo Relacionado" value="NO RELACIONADO" />
          <SiacRow label="Referencia 1" value={solicitud.solicitudNumeroControl || 'Falta Asignar Número de Control'} />
          <SiacRow label="Referencia 2" value="Dejar vacío" />
          <SiacRow label="Cheque / Transferencia" value="TRANSFERENCIA" />
        </SiacCard>

        {/* Card: Condiciones del Contrato */}
        <SiacCard title="Condiciones del Contrato" headerClass="bg-blue-100 text-blue-800">
          <SiacRow label="Actividad" value="EMPLEADO DEL SECTOR PRIVADO" />
          <SiacRow label="Programa Promoción" value="No Aplica" />
          <SiacRow label="Rama Inversión" value="No Aplica" />
          <SiacRow label="Cadena Productiva" value="No Aplica" />
          <SiacRow label="PROMOTOR (Segmentación de cartera)" value={solicitud.empresaSegmentacionCobranza || ''} />
          <SiacRow label="Aplicación de Pagos" value="VERTICAL" />
          <SiacRow label="Tasa Normal (%)" value={solicitud.tasaAutorizada != null ? String(solicitud.tasaAutorizada) : ''} />
          <SiacRow label="Tasa Moratoria (%)" value={solicitud.tasaAutorizada != null ? String(solicitud.tasaAutorizada) : ''} />
          <SiacRow label="Tipo de Interés" value="Interes Global" />
          <SiacRow label="Periodo de Pago" value="AL VENCIMIENTO" />
          <SiacRow label="Zona IVA" value="ZONA 1" />
          <SiacRow label="Tipo de IVA" value="CALCULADO" />
          <SiacRow label="No. Vencimientos" value={String(solicitud.numeroPagos ?? '')} />
          <SiacRow label="Esquema" value="AMORTIZACIONES CONSTANTES" />
          <SiacRow label="Frecuencia de Pago" value={
            solicitud.frecuenciaDePago === 'Quincenal' ? '15 Y ÚLTIMO DEL MES' :
            solicitud.frecuenciaDePago || ''
          } />
        </SiacCard>

        {/* Card: Condiciones Adicionales */}
        <SiacCard title="Condiciones Adicionales" headerClass="bg-gray-100 text-gray-800">
          <SiacRow label={`Seguro - Monto - IVA Calculado - ${solicitud.tipoPagoSeguro || ''}`} value={fmt(solicitud.montoSeguro ?? 0)} />
          <SiacRow label="Comisión - Porcentaje - IVA Calculado - Financiada" value={solicitud.tasaDeComision != null ? `${solicitud.tasaDeComision}%` : ''} />
        </SiacCard>

        {/* Card: Ministraciones y Vencimientos */}
        <SiacCard title="Ministraciones y Vencimientos" headerClass="bg-gray-100 text-gray-800">
          <SiacRow label="Monto Ministración ($)" value={fmt(solicitud.montoMinistracion ?? 0)} />
        </SiacCard>

        {/* Card: Simulador Financiero → Características de Financiamiento */}
        <SiacCard title="Simulador Financiero → Características" headerClass="bg-blue-100 text-blue-800">
          <SiacRow label="Primer Pago" value={solicitud.fechaPrimerCobro ? String(solicitud.fechaPrimerCobro).split('T')[0].split(' ')[0] : ''} />
        </SiacCard>

        {/* Card: Simulador Financiero → Amortizaciones */}
        <SiacCard title="Simulador Financiero → Amortizaciones" headerClass="bg-blue-100 text-blue-800">
          <SiacRow label="Número de Amortizaciones" value={String(solicitud.numeroPagos ?? '')} />
          <SiacRow label="Pago por Parcialidad ($)" value={fmt(solicitud.pagoParcialidadSimulador ?? 0)} />
          <SiacRow label="Fecha del Último Pago" value={solicitud.fechaUltimoPago ? String(solicitud.fechaUltimoPago).split('T')[0].split(' ')[0] : ''} />
          <div className="border-t border-gray-300 mt-1 pt-1">
            <SiacRow label="Total Pago del Capital ($)" value={fmt(solicitud.montoMinistracion ?? 0)} />
            <SiacRow label="Total Pago de Intereses ($)" value={fmt(solicitud.totalInteresesSimulador ?? 0)} />
            <SiacRow label="Total Pago IVA ($)" value={fmt(solicitud.ivaInteresesSimulador ?? 0)} />
            <div className="flex justify-between py-1 mt-1 bg-blue-50 rounded px-1">
              <span className="text-blue-800 text-sm font-bold">Pago Total ($)</span>
              <span className="text-sm font-bold text-blue-900">{fmt(solicitud.pagoTotalSimulador ?? 0)}</span>
            </div>
          </div>
        </SiacCard>

        {/* Card: Ministraciones / Vencimientos → Detalle Ministraciones */}
        <SiacCard title="Ministraciones / Vencimientos → Detalle" headerClass="bg-gray-100 text-gray-800">
          <SiacRow label="Transferencia electrónica ($)" value={fmt(solicitud.montoAutorizado ?? 0)} />
          <SiacRow label={`Seguro - Monto - IVA Calculado - ${solicitud.tipoPagoSeguro || ''}`} value={fmt(solicitud.montoSeguro ?? 0)} />
          <SiacRow label={`IVA Seguro - ${solicitud.tipoPagoSeguro || ''}`} value={fmt(solicitud.montoIvaSeguro ?? 0)} />
          <SiacRow label="Comisión ($)" value={fmt(solicitud.montoComision ?? 0)} />
          <SiacRow label="IVA Comisión ($)" value={fmt(solicitud.montoIvaComision ?? 0)} />
        </SiacCard>

        {/* Card: Información Adicional */}
        <SiacCard title="Información Adicional" headerClass="bg-gray-100 text-gray-800">
          <SiacRow label="Clasificación Operativa" value="AL CONSUMO" />
          <SiacRow label="Propietario Real del Contrato" value={`${solicitud.clienteApellidoPaterno || ''} ${solicitud.clienteApellidoMaterno || ''} ${solicitud.clienteNombre || ''}`.trim()} />
          <SiacRow label="Origen recursos" value="SUELDO" />
          <SiacRow label="Destino recursos" value={solicitud.solicitudDestinoCredito || ''} />
        </SiacCard>

        {/* Card: Información Adicional 2 */}
        <SiacCard title="Información Adicional 2" headerClass="bg-blue-100 text-blue-800">
          <SiacRow label="" value="No se modifica" cls="text-gray-500" />
        </SiacCard>

        {/* Card: Información Adicional 3 */}
        <SiacCard title="Información Adicional 3" headerClass="bg-blue-100 text-blue-800">
          <SiacRow label="Programa productivo" value="NO APLICA" />
          <SiacRow label="Filtro adicional 1 (Empresa)" value={solicitud.asesorNombre || ''} />
          <SiacRow label="Filtro adicional 2 (Frecuencia de Pago)" value={solicitud.frecuenciaDePago || ''} />
          <SiacRow label="Filtro adicional 3 (Pagadora)" value={solicitud.clientePagadora || ''} />
        </SiacCard>
      </div>
    </div>
  );
}
